import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import {
  PageContainer,
  Card,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  fadeIn,
  SubSectionTitle,
  formatString
} from '../styles/PageStyles';
import { listAllEntities } from "../services/entityService";
import { faBackspace } from '@fortawesome/free-solid-svg-icons';

// Custom styled component for table markdown with truncation
const TableMarkdownContainer = styled.div`
  position: relative;
  max-width: 175px;
  height: 20px; /* Fixed height for consistency */
  overflow: hidden;
  cursor: pointer;
  
  .markdown-content {
    font-size: 14px !important;
    line-height: 20px !important;
    margin: 0;
    padding: 0;
    
    /* Override all markdown element sizes */
    * {
      font-size: 14px !important;
      line-height: 20px !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    /* Show only first element by default for truncation */
    > *:not(:first-child) {
      display: none;
    }
    
    h1, h2, h3, h4, h5, h6 {
      font-size: 14px !important;
      font-weight: 600 !important;
      margin: 0 !important;
    }
    
    p {
      margin: 0 !important;
    }
    
    ul, ol {
      padding-left: 16px !important;
      margin: 0 !important;
    }
    
    li {
      margin: 0 !important;
    }
    
    code {
      font-size: 14px !important;
      padding: 2px 4px !important;
    }
    
    strong, b {
      font-weight: 600 !important;
    }
  }
`;

// Custom styled component for drawer markdown rendering
const DrawerMarkdownContainer = styled.div`
  .markdown-content {
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    
    h1, h2, h3, h4, h5, h6 {
      margin: 0.5rem 0 0.25rem 0;
      font-weight: 600;
      color: #2c3e50;
    }
    
    h1 { font-size: 18px; }
    h2 { font-size: 16px; }
    h3 { font-size: 15px; }
    h4, h5, h6 { font-size: 14px; }
    
    p {
      margin: 0.25rem 0;
    }
    
    ul, ol {
      margin: 0.25rem 0;
      padding-left: 1.5rem;
    }
    
    li {
      margin: 0.1rem 0;
    }
    
    code {
      background: #f4f4f4;
      padding: 2px 4px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 13px;
    }
    
    pre {
      background: #f4f4f4;
      padding: 0.5rem;
      border-radius: 4px;
      overflow-x: auto;
      margin: 0.5rem 0;
    }
    
    blockquote {
      border-left: 4px solid #ddd;
      margin: 0.5rem 0;
      padding-left: 1rem;
      color: #666;
    }
    
    strong, b {
      font-weight: 600;
    }
    
    em, i {
      font-style: italic;
    }
    
    a {
      color: #007bff;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 0.5rem 0;
    }
    
    th, td {
      border: 1px solid #ddd;
      padding: 0.25rem 0.5rem;
      text-align: left;
    }
    
    th {
      background: #f8f9fa;
      font-weight: 600;
    }
  }
`;

const EntityRecords = () => {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [entityList, setEntityList] = useState([]);
  const [editableData, setEditableData] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Fetch user data on component load
  useEffect(() => {
    const fetchEntityData = async () => {
      let userData = JSON.parse(localStorage.getItem('userData'));
      let tableName = entityId.toLowerCase().replace(/ /g, "_") + "_" + userData.companyCd.toLowerCase();
      const filters = {};
      const sort = {};
      try {
        const response = await listAllEntities(tableName, filters, sort);
        setEntityList(response || []);
        setEditableData(response || []); // Initialize editable data
      } catch (error) {
        console.error('Error fetching user data:', error);
        navigate('/login'); // Redirect to login if unauthorized
      }
    };
    if (entityId) {
      fetchEntityData();
    }
  }, [navigate, entityId]);

  const excludedFields = ['id', 'company_cd', 'created_by', 'created_at', 'updated_at', 'updated_by'];

  // Function to detect if a string contains markdown
  const isMarkdown = (text) => {
    if (!text || typeof text !== 'string') return false;

    // Check for common markdown patterns
    const markdownPatterns = [
      /#{1,6}\s+/, // Headers
      /\*\*[^*]+\*\*/, // Bold
      /\*[^*]+\*/, // Italic
      /`[^`]+`/, // Inline code
      /```[\s\S]*?```/, // Code blocks
      /\[.+\]\(.+\)/, // Links
      /!\[.*\]\(.+\)/, // Images
      /^\s*[-*+]\s+/m, // Unordered lists
      /^\s*\d+\.\s+/m, // Ordered lists
      /^\s*>\s+/m, // Blockquotes
      /\|.*\|/, // Tables
      /---+/, // Horizontal rules
    ];

    return markdownPatterns.some(pattern => pattern.test(text));
  };

  // Component to render entity field with markdown support
  const EntityFieldRenderer = ({ label, value }) => {
    const renderValue = (val) => {
      if (val === null || val === undefined) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>null</span>;
      }
      
      if (typeof val === 'string') {
        if (isMarkdown(val)) {
          return (
            <DrawerMarkdownContainer>
              <div className="markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {val}
                </ReactMarkdown>
              </div>
            </DrawerMarkdownContainer>
          );
        } else {
          return <span style={{ wordBreak: 'break-word' }}>{val}</span>;
        }
      }
      
      if (typeof val === 'object') {
        if (Array.isArray(val)) {
          return (
            <div>
              {val.map((item, idx) => (
                <div key={idx} style={{ 
                  marginBottom: '0.5rem', 
                  padding: '0.5rem', 
                  background: '#f8f9fa', 
                  borderRadius: '4px',
                  border: '1px solid #e9ecef'
                }}>
                  {typeof item === 'object' ? (
                    Object.entries(item).map(([k, v]) => (
                      <div key={k} style={{ marginBottom: '0.25rem' }}>
                        <strong>{formatString(k)}:</strong>{' '}
                        {renderValue(v)}
                      </div>
                    ))
                  ) : (
                    renderValue(item)
                  )}
                </div>
              ))}
            </div>
          );
        } else {
          return (
            <div style={{ 
              padding: '0.5rem', 
              background: '#f8f9fa', 
              borderRadius: '4px',
              border: '1px solid #e9ecef'
            }}>
              {Object.entries(val).map(([k, v]) => (
                <div key={k} style={{ marginBottom: '0.25rem' }}>
                  <strong>{formatString(k)}:</strong>{' '}
                  {renderValue(v)}
                </div>
              ))}
            </div>
          );
        }
      }
      
      return <span>{String(val)}</span>;
    };

    return (
      <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e9ecef', borderRadius: '4px' }}>
        <div style={{ 
          fontWeight: '600', 
          marginBottom: '0.5rem', 
          color: '#2c3e50',
          fontSize: '14px',
          textTransform: 'capitalize'
        }}>
          {label.replace(/_/g, ' ')}
        </div>
        <div style={{ fontSize: '14px', color: '#333' }}>
          {renderValue(value)}
        </div>
      </div>
    );
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faBackspace}
          goBack={true}
          title={entityId}
          description="The Entity page is designed to manage the knowledge sources and documents that power AI agents."
        >
          <div style={{ display: 'flex', gap: "1rem" }}>
            <SearchInput
              type="text"
              placeholder="Search Entities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </PageHeaderWithInfo>

        <Card>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <Table>
              <thead>
                <tr>
                  {entityList.length > 0 &&
                    Object.keys(entityList[0])
                      .filter((key) => !excludedFields.includes(key))
                      .filter((key) => !Array.isArray(entityList[0][key])) // Exclude keys with array values
                      .map((key) => (
                        <TableHeader key={key}>{key.replace(/_/g, ' ')}</TableHeader>
                      ))}
                </tr>
              </thead>
              <tbody>
                {editableData.map((row, rowIndex) => (
                  <TableRow key={rowIndex} onClick={(e) => { setIsDrawerOpen(true); setSelectedEntity(row) }}>
                    {Object.keys(row)
                      .filter((key) => !excludedFields.includes(key))
                      .filter((key) => !Array.isArray(row[key]))
                      .map((field) => (
                        <TableCell key={field} style={{ cursor: 'pointer', verticalAlign: 'top' }}>
                          {typeof row[field] === 'string' || typeof row[field] === 'number' || typeof row[field] === 'boolean' ? (
                            typeof row[field] === 'string' && isMarkdown(row[field]) ? (
                              <TableMarkdownContainer>
                                <div className="markdown-content">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {row[field]}
                                  </ReactMarkdown>
                                </div>
                              </TableMarkdownContainer>
                            ) : (
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: '14px' }}>
                                {row[field]}
                              </span>
                            )
                          ) : Array.isArray(row[field]) ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {row[field].map((item, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                    padding: '0.5rem',
                                    minWidth: '120px',
                                    background: '#fafafa',
                                    marginBottom: '0.5rem'
                                  }}
                                >
                                  {typeof item === 'object' && item !== null ? (
                                    Object.entries(item).map(([k, v]) => (
                                      <div key={k} style={{ fontSize: '14px', marginBottom: '0.2em' }}>
                                        <b>{formatString(k)}:</b> {
                                          typeof v === 'string' && isMarkdown(v) ? (
                                            <TableMarkdownContainer style={{ display: 'inline-block', maxWidth: '150px' }}>
                                              <div className="markdown-content">
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                  {v}
                                                </ReactMarkdown>
                                              </div>
                                            </TableMarkdownContainer>
                                          ) : (
                                            typeof v === 'object' ? JSON.stringify(v) : String(v)
                                          )
                                        }
                                      </div>
                                    ))
                                  ) : (
                                    typeof item === 'string' && isMarkdown(item) ? (
                                      <TableMarkdownContainer>
                                        <div className="markdown-content">
                                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {item}
                                          </ReactMarkdown>
                                        </div>
                                      </TableMarkdownContainer>
                                    ) : (
                                      <span style={{ fontSize: '14px' }}>{String(item)}</span>
                                    )
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : typeof row[field] === 'object' && row[field] !== null ? (
                            <div>
                              {Object.entries(row[field]).map(([k, v]) => (
                                <div key={k} style={{ fontSize: '14px', marginBottom: '0.2em' }}>
                                  <b>{formatString(k)}: </b> {
                                    typeof v === 'string' && isMarkdown(v) ? (
                                      <TableMarkdownContainer style={{ display: 'inline-block', maxWidth: '150px' }}>
                                        <div className="markdown-content">
                                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {v}
                                          </ReactMarkdown>
                                        </div>
                                      </TableMarkdownContainer>
                                    ) : (
                                      typeof v === 'object' ? JSON.stringify(v) : String(v)
                                    )
                                  }
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span style={{ fontSize: '14px' }}>-</span>
                          )}
                        </TableCell>
                      ))}
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      </motion.div>
      {isDrawerOpen && selectedEntity && (
        <div className="custom-drawer">
          <div className="drawer-content">
            <SubSectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Entity Details
              <Button secondary style={{ marginTop: '0' }} onClick={(e) => { setIsDrawerOpen(false); setSelectedEntity(null) }}>Cancel</Button>
            </SubSectionTitle>
            <hr />
            <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: '1rem 0' }}>
              {Object.entries(selectedEntity)
                .filter(([key]) => !excludedFields.includes(key))
                .map(([key, value]) => (
                  <EntityFieldRenderer 
                    key={key} 
                    label={key} 
                    value={value} 
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default EntityRecords;